import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Table, TableHead, TableRow, TableCell, TableBody, Chip, Button,
  CircularProgress, TextField, Grid, Switch, FormControlLabel, InputAdornment, Select, MenuItem,
  FormControl, InputLabel, Drawer, Divider, Snackbar, Alert, Tooltip
} from '@mui/material';
import axios from 'axios';
import { API, getAuthHeader, money, fmtDay, fmtDate, numField, isBlank } from './customerApp/shared';

const STATUS_COLOR = { pending: 'warning', approved: 'success', suspended: 'default', denied: 'error', paid: 'success', voided: 'default' };
const StatusChip = ({ status }) => <Chip label={status} size="small" color={STATUS_COLOR[status] || 'default'} />;

const copy = (text, notify) => {
  navigator.clipboard?.writeText(text).then(() => notify('Copied', 'success')).catch(() => notify(text, 'info'));
};

function ProgramCard({ program, setProgram, onSave, saving }) {
  const set = (field, value) => setProgram((p) => ({ ...p, [field]: value }));
  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="h6">Program</Typography>
        <Button variant="contained" onClick={onSave} disabled={saving}>{saving ? 'Saving...' : 'Save program'}</Button>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Approved affiliates get a code and a link. A customer who joins through the link or enters the code is credited to them,
        and each membership payment that customer makes earns the commission below. Payouts are manual: approve the commission here,
        pay the affiliate, then mark it paid.
      </Typography>
      <Grid container spacing={2} alignItems="flex-start" sx={{ mb: 1 }}>
        <Grid item xs={12} sm={6}>
          <FormControlLabel control={<Switch checked={program.enabled !== false} onChange={(e) => set('enabled', e.target.checked)} />} label="Affiliate program" />
          <Typography variant="caption" color="text.secondary" display="block">
            Off hides the affiliate section on wkroadss.com and in the app, and stops new sign-ups. Existing affiliates and unpaid commissions are kept.
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControlLabel control={<Switch checked={program.enabled !== false && program.acceptingApplications !== false} disabled={program.enabled === false} onChange={(e) => set('acceptingApplications', e.target.checked)} />} label="Accepting applications" />
          <Typography variant="caption" color="text.secondary" display="block">
            Off keeps the program visible but closes the sign-up form.
          </Typography>
        </Grid>
      </Grid>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={6} sm={3} md={2}>
          <TextField
            {...numField({ label: 'Commission' })} value={program.commissionPercent ?? ''} onChange={(e) => set('commissionPercent', e.target.value)}
            InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }} inputProps={{ min: 0, max: 100 }}
          />
        </Grid>
        <Grid item xs={6} sm={3} md={2}>
          <TextField
            {...numField({ label: 'Renewals earn for' })} value={program.recurringMonths ?? ''} onChange={(e) => set('recurringMonths', e.target.value)}
            InputProps={{ endAdornment: <InputAdornment position="end">mo</InputAdornment> }} inputProps={{ min: 0, max: 120 }}
            helperText="0 = first payment only"
          />
        </Grid>
        <Grid item xs={6} sm={3} md={2}>
          <TextField
            {...numField({ label: 'Link remembered for' })} value={program.cookieDays ?? ''} onChange={(e) => set('cookieDays', e.target.value)}
            InputProps={{ endAdornment: <InputAdornment position="end">days</InputAdornment> }} inputProps={{ min: 1, max: 365 }}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth multiline minRows={4} size="small" label="Program terms (shown on the website application)"
            value={program.terms || ''} onChange={(e) => set('terms', e.target.value)}
          />
        </Grid>
      </Grid>
    </Paper>
  );
}

function AffiliateDrawer({ id, onClose, notify, onChanged, programPercent }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notes, setNotes] = useState('');
  const [commission, setCommission] = useState('');

  const load = useCallback(async () => {
    if (!id) return;
    try {
      const { data: d } = await axios.get(`${API}/api/affiliates-admin/${id}`, { headers: getAuthHeader() });
      setData(d);
      setNotes(d.affiliate.notes || '');
      setCommission(d.affiliate.commissionPercent ?? '');
    } catch (err) {
      notify(err.response?.data?.message || 'Failed to load affiliate', 'error');
    }
  }, [id, notify]);

  useEffect(() => { setData(null); load(); }, [load]);

  const act = async (path, body = {}, success) => {
    setBusy(true);
    try {
      await axios.post(`${API}/api/affiliates-admin/${id}/${path}`, body, { headers: getAuthHeader() });
      notify(success, 'success');
      await load();
      onChanged();
    } catch (err) {
      notify(err.response?.data?.message || 'Action failed', 'error');
    } finally {
      setBusy(false);
    }
  };

  const saveDetails = async () => {
    setBusy(true);
    try {
      await axios.put(`${API}/api/affiliates-admin/${id}`, { notes, commissionPercent: isBlank(commission) ? null : Number(commission) }, { headers: getAuthHeader() });
      notify('Saved', 'success');
      await load();
      onChanged();
    } catch (err) {
      notify(err.response?.data?.message || 'Save failed', 'error');
    } finally {
      setBusy(false);
    }
  };

  const a = data?.affiliate;
  return (
    <Drawer anchor="right" open={!!id} onClose={onClose} PaperProps={{ sx: { width: { xs: '100%', sm: 520 }, p: 3 } }}>
      {!a ? <CircularProgress /> : (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">{a.name}</Typography>
            <StatusChip status={a.status} />
          </Box>
          <Typography variant="body2" sx={{ mt: 1 }}><b>Email:</b> {a.email}</Typography>
          <Typography variant="body2"><b>Phone:</b> {a.phone || '—'}</Typography>
          <Typography variant="body2"><b>Company:</b> {a.company || '—'}{a.website ? ` · ${a.website}` : ''}</Typography>
          <Typography variant="body2"><b>Payout:</b> {a.payoutMethod}{a.payoutDetail ? ` (${a.payoutDetail})` : ''}</Typography>
          <Typography variant="body2" sx={{ mt: 1 }}><b>How they'll promote:</b></Typography>
          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>{a.audience || '—'}</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Applied {fmtDate(a.createdAt)}{a.agreedTermsAt ? ` · agreed to terms ${fmtDay(a.agreedTermsAt)}` : ''}{a.approvedAt ? ` · approved ${fmtDay(a.approvedAt)}` : ''}
          </Typography>

          {a.code && (
            <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
              <Typography variant="body2"><b>Code:</b> {a.code} <Button size="small" onClick={() => copy(a.code, notify)}>Copy</Button></Typography>
              <Typography variant="body2" sx={{ wordBreak: 'break-all' }}><b>Link:</b> {a.link} <Button size="small" onClick={() => copy(a.link, notify)}>Copy link</Button></Typography>
            </Paper>
          )}

          {a.totals && (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2 }}>
              <Chip label={`${a.totals.referrals} referred`} size="small" />
              <Chip label={`${a.totals.activeMembers} active`} size="small" />
              <Chip label={`Pending ${money(a.totals.pendingCents)}`} size="small" color="warning" variant="outlined" />
              <Chip label={`Approved ${money(a.totals.approvedCents)}`} size="small" color="info" variant="outlined" />
              <Chip label={`Paid ${money(a.totals.paidCents)}`} size="small" color="success" variant="outlined" />
            </Box>
          )}

          <Divider sx={{ my: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                {...numField({ label: 'Commission override' })} value={commission} onChange={(e) => setCommission(e.target.value)}
                InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }} inputProps={{ min: 0, max: 100 }}
                helperText={`Blank = program rate (${programPercent}%)`}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline minRows={2} size="small" label="Internal notes" value={notes} onChange={(e) => setNotes(e.target.value)} />
            </Grid>
          </Grid>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2 }}>
            <Button variant="outlined" onClick={saveDetails} disabled={busy}>Save notes</Button>
            {a.status !== 'approved' && (
              <Button variant="contained" color="success" disabled={busy}
                onClick={() => act('approve', isBlank(commission) ? {} : { commissionPercent: Number(commission) }, a.code ? 'Reinstated' : 'Approved — code emailed')}>
                {a.status === 'suspended' ? 'Reinstate' : 'Approve'}
              </Button>
            )}
            {a.status === 'pending' && <Button color="error" disabled={busy} onClick={() => act('deny', { notes }, 'Denied')}>Deny</Button>}
            {a.status === 'approved' && <Button color="warning" disabled={busy} onClick={() => act('suspend', { notes }, 'Suspended')}>Suspend</Button>}
          </Box>

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle1" gutterBottom>Commissions</Typography>
          <Table size="small">
            <TableHead>
              <TableRow><TableCell>Date</TableCell><TableCell>Customer</TableCell><TableCell>Paid</TableCell><TableCell>Earned</TableCell><TableCell>Status</TableCell></TableRow>
            </TableHead>
            <TableBody>
              {(data.referrals || []).length === 0 && <TableRow><TableCell colSpan={5}><Typography variant="body2" color="text.secondary">None yet</Typography></TableCell></TableRow>}
              {(data.referrals || []).map((r) => (
                <TableRow key={r.id}>
                  <TableCell>{fmtDay(r.createdAt)}</TableCell>
                  <TableCell>{r.customerName || r.customerEmail || '—'}</TableCell>
                  <TableCell>{money(r.amountCents)}</TableCell>
                  <TableCell>{money(r.commissionCents)}</TableCell>
                  <TableCell><StatusChip status={r.status} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </>
      )}
    </Drawer>
  );
}

function ReferralsTable({ notify, refreshKey }) {
  const [status, setStatus] = useState('pending');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/affiliates-admin/referrals${status ? `?status=${status}` : ''}`, { headers: getAuthHeader() });
      setRows(data.referrals || []);
    } catch (err) {
      notify(err.response?.data?.message || 'Failed to load commissions', 'error');
    } finally {
      setLoading(false);
    }
  }, [status, notify]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const act = async (id, path, success) => {
    try {
      await axios.post(`${API}/api/affiliates-admin/referrals/${id}/${path}`, {}, { headers: getAuthHeader() });
      notify(success, 'success');
      load();
    } catch (err) {
      notify(err.response?.data?.message || 'Action failed', 'error');
    }
  };

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Commissions</Typography>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Status</InputLabel>
          <Select label="Status" value={status} onChange={(e) => setStatus(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            {['pending', 'approved', 'paid', 'voided'].map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
          </Select>
        </FormControl>
      </Box>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Date</TableCell><TableCell>Affiliate</TableCell><TableCell>Customer</TableCell><TableCell>Plan</TableCell>
            <TableCell>Paid</TableCell><TableCell>Commission</TableCell><TableCell>Status</TableCell><TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {loading && rows.length === 0 && <TableRow><TableCell colSpan={8} align="center"><CircularProgress size={20} /></TableCell></TableRow>}
          {!loading && rows.length === 0 && <TableRow><TableCell colSpan={8}><Typography variant="body2" color="text.secondary">Nothing here</Typography></TableCell></TableRow>}
          {rows.map((r) => (
            <TableRow key={r.id}>
              <TableCell>{fmtDay(r.createdAt)}</TableCell>
              <TableCell>{r.affiliateName || '—'}{r.affiliateCode ? ` (${r.affiliateCode})` : ''}</TableCell>
              <TableCell>{r.customerName || r.customerEmail || '—'}</TableCell>
              <TableCell>{r.planKey || '—'}</TableCell>
              <TableCell>{money(r.amountCents)}</TableCell>
              <TableCell>{money(r.commissionCents)}</TableCell>
              <TableCell><StatusChip status={r.status} />{r.paidAt ? <Typography variant="caption" sx={{ ml: 1 }}>{fmtDay(r.paidAt)}</Typography> : null}</TableCell>
              <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                {r.status === 'pending' && <Button size="small" onClick={() => act(r.id, 'approve', 'Commission approved')}>Approve</Button>}
                {r.status === 'approved' && <Button size="small" color="success" onClick={() => act(r.id, 'paid', 'Marked paid')}>Mark paid</Button>}
                {['pending', 'approved'].includes(r.status) && <Button size="small" color="error" onClick={() => act(r.id, 'void', 'Voided')}>Void</Button>}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Paper>
  );
}

function Affiliates() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [program, setProgram] = useState(null);
  const [affiliates, setAffiliates] = useState([]);
  const [filter, setFilter] = useState('');
  const [selected, setSelected] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [snack, setSnack] = useState(null);

  const notify = useCallback((message, severity = 'info') => setSnack({ message, severity }), []);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API}/api/affiliates-admin${filter ? `?status=${filter}` : ''}`, { headers: getAuthHeader() });
      setAffiliates(data.affiliates || []);
      setProgram((p) => p || data.program);
    } catch (err) {
      notify(err.response?.data?.message || 'Failed to load affiliates', 'error');
    } finally {
      setLoading(false);
    }
  }, [filter, notify]);

  useEffect(() => { load(); }, [load]);

  const saveProgram = async () => {
    try {
      setSaving(true);
      const { data } = await axios.put(`${API}/api/customer-admin/config`, {
        affiliate: {
          enabled: program.enabled !== false,
          acceptingApplications: program.acceptingApplications !== false,
          commissionPercent: Number(program.commissionPercent),
          recurringMonths: Number(program.recurringMonths),
          cookieDays: Number(program.cookieDays),
          terms: program.terms || ''
        }
      }, { headers: getAuthHeader() });
      setProgram(data.config.affiliate);
      notify('Program saved', 'success');
    } catch (err) {
      notify(err.response?.data?.message || 'Failed to save program', 'error');
    } finally {
      setSaving(false);
    }
  };

  const changed = () => { load(); setRefreshKey((k) => k + 1); };

  if (loading && !program) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;

  return (
    <Box>
      <Box sx={{ mb: 2, mt: 2 }}>
        <Typography variant="h5">Affiliates</Typography>
        <Typography variant="body2" color="text.secondary">People who sell memberships for a commission. Applications come from wkroadss.com/affiliates.</Typography>
      </Box>

      {program && <ProgramCard program={program} setProgram={setProgram} onSave={saveProgram} saving={saving} />}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Applications and affiliates</Typography>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Status</InputLabel>
            <Select label="Status" value={filter} onChange={(e) => setFilter(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              {['pending', 'approved', 'suspended', 'denied'].map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </Select>
          </FormControl>
        </Box>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell><TableCell>Email</TableCell><TableCell>Code</TableCell><TableCell>Status</TableCell>
              <TableCell>Referred</TableCell><TableCell>Pending</TableCell><TableCell>Paid</TableCell><TableCell>Applied</TableCell><TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {affiliates.length === 0 && <TableRow><TableCell colSpan={9}><Typography variant="body2" color="text.secondary">No applications yet</Typography></TableCell></TableRow>}
            {affiliates.map((a) => (
              <TableRow key={a.id} hover sx={{ cursor: 'pointer' }} onClick={() => setSelected(a.id)}>
                <TableCell>{a.name}{a.company ? <Typography variant="caption" display="block" color="text.secondary">{a.company}</Typography> : null}</TableCell>
                <TableCell>{a.email}</TableCell>
                <TableCell>{a.code || '—'}</TableCell>
                <TableCell><StatusChip status={a.status} /></TableCell>
                <TableCell>{a.totals?.referrals ?? 0}</TableCell>
                <TableCell>{money(a.totals?.pendingCents ?? 0)}</TableCell>
                <TableCell>{money(a.totals?.paidCents ?? 0)}</TableCell>
                <TableCell>{fmtDay(a.createdAt)}</TableCell>
                <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                  {a.link && <Tooltip title={a.link}><Button size="small" onClick={() => copy(a.link, notify)}>Copy link</Button></Tooltip>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <ReferralsTable notify={notify} refreshKey={refreshKey} />

      <AffiliateDrawer id={selected} onClose={() => setSelected(null)} notify={notify} onChanged={changed} programPercent={program?.commissionPercent ?? 20} />

      <Snackbar open={!!snack} autoHideDuration={5000} onClose={() => setSnack(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        {snack ? <Alert severity={snack.severity} onClose={() => setSnack(null)} variant="filled">{snack.message}</Alert> : undefined}
      </Snackbar>
    </Box>
  );
}

export default Affiliates;
